import React, { useState } from 'react';
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  Typography,
  Grid,
  Tabs,
  Tab,
  Button
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CircleIcon from '@mui/icons-material/Circle';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { filter, isEmpty, map } from 'lodash';
import { useQuery, useMutation } from '@apollo/client/react';
import { GetUserNotificationsDocument, MarkNotificationsAsReadDocument } from '@/graphql/generated/graphql';
import { objectLength } from '@/utils/helperFunctions';
import { BLACK, BLACK1, BLUE0, BLUE2, GREEN, GREY, GREY1, GREY2, WHITE } from '@/theme/palette';

const Notification: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<(EventTarget & HTMLButtonElement) | null>(null);
  const [currentTab, setCurrentTab] = useState<number>(0);

  const { data } = useQuery(GetUserNotificationsDocument);
  const [markAsRead] = useMutation(MarkNotificationsAsReadDocument, {
    refetchQueries: ["getUserNotifications"]
  });

  const unreadCount = data?.getUserNotifications && objectLength(filter((data?.getUserNotifications), (notification) => !notification.read)) || 0;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'notification-popover' : undefined;

  // Filter logic based on the tabs in the mockup
  const getFilteredNotifications = () => {
    const allNotifications = data?.getUserNotifications || [];
    if (currentTab === 1) {
      return filter(allNotifications, (n) => !n.read);
    }

    if (currentTab === 2) {
      return filter(allNotifications, (n) => n.isMentioned);
    }
    return allNotifications;
  };

  return (
    <Grid>
      <IconButton
        aria-describedby={open ? id : undefined}
        onClick={handleClick}
        size="large"
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon sx={{ color: WHITE}} />
        </Badge>
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            style: {
              width: "min(450px, 90vw)",
              borderRadius: "12px",
              boxShadow: `0px 4px 20px ${BLACK1}`
            }
          }
        }}
      >
        {/* Header Block */}
        <Grid container alignItems="center" justifyContent="space-between" px={3} pt={2.5} pb={1.5}>
          <Typography variant="h6" fontWeight="600" sx={{ fontSize: '1.25rem' }}>
            Notifications
          </Typography>
          <IconButton size="small" color="inherit" onClick={handleClose}>
            <CloseIcon sx={{ fontSize: 20, color: "text.secondary" }} />
          </IconButton>
        </Grid>

        {/* Navigation Filters */}
        <Grid container alignItems="center" p="1" justifyContent="space-between" borderBottom={`1px solid ${GREY1}`}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{
              minHeight: "44px",
              bgcolor: GREY2,
              border: `1px solid ${GREY1}`,
              borderRadius: "8px",
              "& .MuiTab-root": {
                textTransform: "none",
                minWidth: "auto",
                px: 1,
                py: 0.5,
                fontSize: "0.875rem",
                fontWeight: 500,
                color: GREY,
                '&.Mui-selected': {
                  color: BLACK,
                  fontWeight: 500,
                  bgcolor: WHITE,
                }
              }
            }}
          >
            <Tab label={
              <Grid container alignItems="center" wrap="nowrap" gap={1.2}>
                All
                <Grid
                  component="span"
                  sx={{
                    bgcolor: BLUE2,
                    color: BLUE0,
                    px: 0.8,
                    py: 0.3,
                    borderRadius: "2px",
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    lineHeight: 1
                  }}
                >
                  {data?.getUserNotifications?.length || 0}
                </Grid>
              </Grid>
            } />
            <Tab label="Unread" />
            <Tab label="Mentions" />
          </Tabs>
          <Grid>
            <Button
              variant="text"
              sx={{
                fontSize: "0.65rem",
                textTransform: "none"
              }}
              onClick={() => markAsRead()}
            >
              Mark All as Read
            </Button>
            <IconButton size="small">
              <SettingsOutlinedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
            </IconButton>
          </Grid>
        </Grid>

        {/* Notifications List */}
        <List sx={{ p: 0, maxHeight: "480px", overflowY: "auto" }}>
          {!isEmpty(getFilteredNotifications()) ? (
            map(getFilteredNotifications(), (notification) => (
              <ListItem
                key={notification.id}
                alignItems="flex-start"
                sx={{
                  px: 3,
                  py: 2,
                  borderBottom: `1px solid ${GREY1}`,
                  bgcolor: "background.paper",
                  display: "flex",
                  justifyContent: "space-between"
                }}
              >
                <Grid display="flex" width="100%">
                  <ListItemText
                    primary={
                      <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.4 }}>
                        {notification.text}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                </Grid>

                {/* Right-Aligned Unread Indicator Dot */}
                {!notification.read && (
                  <Grid display="flex" alignItems="center" pl={1} pt={0.5}>
                    <CircleIcon
                      sx={{ fontSize: "12px", color: GREEN, cursor: "pointer" }}
                      onClick={() => markAsRead({ variables: { id: notification.id } })}
                    />
                  </Grid>
                )}
              </ListItem>
            ))
          ) : (
            <Grid py={6} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                No notifications
              </Typography>
            </Grid>
          )}
        </List>
      </Popover>
    </Grid>
  );
};

export default Notification;
